import { East, NorthEast, SouthEast, TaskAlt } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import './DataTask.scss';
import {
  TaskChartSelector,
  TaskChartSelectorOption,
  transformTaskCompletionToString,
} from '../../DataTaskDashboard';
import { useRecoilValue } from 'recoil';
import {
  circadianPhaseInfo,
  dataTrendPeriodAtom,
  selectedDateAtom,
  zoneLevelColor,
} from '../../../../data-access';
import { useTaskTrend } from '../../../../hooks';
import { groupBy, sumBy } from 'lodash';
import { getCssVariable } from '../../../../utils';
import clsx from 'clsx';
import { DataTaskDistributionChart } from './DataTaskDistributionChart';
import { DataWeeklyChart } from './DataWeeklyChart';
import { DataWeeklyChartData } from '../../types';
import { ChartDataItem } from './DataTaskDistributionChart/DataTaskDistributionChart';
import { useTranslation } from 'react-i18next';
import { getZoneLevelLabel } from '../../i18n';

const colorGrayBase = getCssVariable('--color-gray-base');

const DataTask = () => {
  const { t } = useTranslation();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const [chartViewMode, setChartViewMode] = useState<TaskChartSelectorOption>('label');
  const dataTrendPeriod = useRecoilValue(dataTrendPeriodAtom);
  const { commonData, isLoading, weeklyDoneTasksData } = useTaskTrend({
    date: selectedDate,
  });

  const isWeekly = dataTrendPeriod === 'weekly';
  const diffNum = commonData.taskCompletion?.diff;
  const pieChartData: ChartDataItem[] = useMemo(() => {
    if (chartViewMode === 'label') {
      return [
        ...commonData.trendByLabels
          .filter((item) => item.totalCompletedTaskNum > 0)
          .map((item) => ({
            label: item.labelName,
            value: item.totalCompletedTaskNum,
            color: item.labelColor || colorGrayBase,
          })),
      ];
    } else {
      const trendGroupByLevel = groupBy(
        commonData.trendByZone,
        (d) => circadianPhaseInfo[d.zoneName].level
      );
      return Object.entries(trendGroupByLevel)
        .map(([zoneLevel, value]) => ({
          label: t('data.task.energyZone', {
            level: getZoneLevelLabel(t, zoneLevel),
          }),
          value: sumBy(value, 'totalCompletedTaskNum'),
          color: zoneLevelColor[zoneLevel] || colorGrayBase,
        }))
        .filter((item) => item.value > 0);
    }
  }, [commonData, chartViewMode, t]);

  const hasDiffNum =
    diffNum !== undefined && diffNum !== null && diffNum !== 'none' && diffNum !== 0;

  const getDiffNumColor = (diff: number) => {
    if (diff > 0) return 'var(--color-green-base)';
    if (diff < 0) return 'var(--color-red-base)';
    if (diff === 0) return 'var(--color-gray-dark)';
    return '';
  };

  return (
    <div className="data-task">
      <Typography variant="body1" className="data-task__title">
        {t('data.task.title')}
      </Typography>
      <Stack direction="row" justifyContent="space-between" alignItems="end" mb={2}>
        <Stack flex={1}>
          <Typography variant="h2" className="data-task__completion-value">
            {transformTaskCompletionToString(commonData.taskCompletion?.current).percent}%
          </Typography>
          <Typography variant="body2" className="data-task__subtitle">
            {t('data.task.completion')}
          </Typography>
        </Stack>
        <Stack flex={1}>
          <Typography
            variant="body1"
            className="square-metric-card__change"
            color={getDiffNumColor(diffNum as number)}
          >
            {hasDiffNum ? (
              diffNum > 0 ? (
                <NorthEast className="data-task__diff__icon" />
              ) : (
                <SouthEast className="data-task__diff__icon" />
              )
            ) : diffNum === 0 ? (
              <>-</>
            ) : (
              ''
            )}
            {hasDiffNum ? Math.abs(Math.floor(diffNum)) : '--'}%
          </Typography>
          <Typography className="data-task__diff-label" variant="body2">
            {dataTrendPeriod === 'daily'
              ? t('data.common.vsPrevDay')
              : t('data.common.vsPrevWeek')}
          </Typography>
        </Stack>
      </Stack>
      <Stack className="data-task__content">
        <TaskChartSelector value={chartViewMode} onChange={setChartViewMode} />
        {isWeekly ? (
          <DataWeeklyChart
            zoneData={weeklyDoneTasksData?.zone as DataWeeklyChartData}
            labelData={weeklyDoneTasksData?.labels as DataWeeklyChartData}
            chartViewMode={chartViewMode}
            hideLegend
          />
        ) : (
          <DataTaskDistributionChart
            data={pieChartData}
            size={156}
            value={commonData.doneTaskComparison?.current ?? '--'}
            diffNum={commonData.doneTaskComparison?.diff as number}
            color={getDiffNumColor(commonData.doneTaskComparison?.diff as number)}
          />
        )}
      </Stack>
    </div>
  );
};

export default DataTask;
